import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * STORY-STRIPE-009c — Close-gym surface.
 *
 * Destructive action: soft-deletes the gym, orphans the members, cancels the
 * Stripe subscription at period end. Guarded by:
 *   1. A two-step disclosure (button → confirm panel)
 *   2. The user must type the gym name verbatim before "Close gym" arms
 *
 * Personal data is preserved server-side; this component just makes it
 * extremely hard to fire by accident.
 */
export function CloseGymCard({
  affiliateId,
  affiliateName,
}: {
  affiliateId: string;
  affiliateName: string;
}) {
  const { api } = useApi();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);

  const armed = typed.trim() === affiliateName.trim();

  const handleClose = async () => {
    if (!armed) return;
    setLoading(true);
    try {
      const res = await api.post<{
        members_orphaned: number;
        stripe_cancelled_at_period_end: boolean;
      }>(`/affiliates/${affiliateId}/close`, {});
      if (res.success) {
        toast.success(
          `Gym closed. ${res.data?.members_orphaned ?? 0} member(s) have been notified.`
        );
        // Hard reload so the profile switcher and sidebar drop the closed gym.
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        toast.error(res.error || 'Could not close the gym. Please try again.');
        setLoading(false);
      }
    } catch {
      toast.error('Could not close the gym. Please try again.');
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Card className="border-red-500/30 bg-red-500/5">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <CardTitle>Close this gym</CardTitle>
          </div>
          <CardDescription>
            Permanently shut down {affiliateName}. Members will be notified and removed from the
            roster, but their personal data (scores, friends, history) is preserved. Your
            subscription will cancel at the end of the current billing cycle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="border-red-500/40 text-red-500 hover:bg-red-500/10"
            onClick={() => setOpen(true)}
          >
            Close gym…
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-500/40 bg-red-500/10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <CardTitle>Confirm closing {affiliateName}</CardTitle>
        </div>
        <CardDescription>
          This action cannot be undone from inside the app. Type the gym name below exactly to arm
          the close button.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder={affiliateName}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
        />
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={handleClose}
            disabled={!armed || loading}
            className="bg-red-500 hover:bg-red-600 disabled:opacity-50"
          >
            {loading ? 'Closing…' : 'Close gym permanently'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
              setTyped('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
